import { useState, useMemo, useEffect } from 'react';
import { Plus, Trash2, Clock, ShieldCheck, AlertCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { SlideOver, SlideOverFooter } from '@/components/Shared/SlideOver';
import { Button } from '@/components/Shared/Button';
import { FormField } from '@/components/Shared/FormField';
import { Badge } from '@/components/Shared/Badge';
import { useTimeclockAdminStore } from '@/store/useTimeclockAdminStore';
import type { DriverRole, WorkLocation } from '@/store/useTimeclockStore';
import { useDescribeVehicle } from '@/hooks/useDescribeVehicle';
import {
  FacilityFlowCoreModulesPlanningEntitiesTimeClockEventType as TimeClockEventTypeEnum,
  FacilityFlowCoreModulesPlanningEntitiesTimeClockDayStatus as TimeClockDayStatusEnum,
} from '@/api/generated/models';
import { isHomeOffice, stretchLocationsByTime } from '@/utils/workLocation';
import { WorkLocationSelect } from './WorkLocationSelect';
import type { FacilityFlowCoreModulesPlanningWorkflowsTimeClockTimeClockDayDto } from '@/api/generated/models';
import {
  utcToLocalTimeInputValue,
  localTimeToUtcIso,
  parseUtcTimestamp,
  isoDate,
} from '@/utils/dateUtils';
import { parseAxiosError, translateApiError } from '@/errors';

const EVENT_TYPE_KEYS: Record<number, string> = {
  [TimeClockEventTypeEnum.NUMBER_1]: 'edit.eventTypes.clockIn',
  [TimeClockEventTypeEnum.NUMBER_2]: 'edit.eventTypes.clockOut',
  [TimeClockEventTypeEnum.NUMBER_3]: 'edit.eventTypes.breakStart',
  [TimeClockEventTypeEnum.NUMBER_4]: 'edit.eventTypes.breakEnd',
};

const EVENT_TYPE_VALUES = [
  TimeClockEventTypeEnum.NUMBER_1,
  TimeClockEventTypeEnum.NUMBER_2,
  TimeClockEventTypeEnum.NUMBER_3,
  TimeClockEventTypeEnum.NUMBER_4,
];

interface EditableEvent {
  id: string;
  eventType: number;
  timeValue: string;
  isNew: boolean;
  /** Chosen at clock-in and stored there only. */
  workLocation: WorkLocation | null;
  /** Vehicle and role of an old stamp (before ADR-0027) — shown only; trips are no longer stamped. */
  vehicleId: string | null;
  driverRole: DriverRole | null;
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  entry: FacilityFlowCoreModulesPlanningWorkflowsTimeClockTimeClockDayDto | null;
  employeeId: string;
  employeeName?: string;
  /** The employee's home office permission — without it the administration cannot set home office either. */
  allowsHomeOffice?: boolean;
  /**
   * Adds a whole day: the date is picked in the dialog and starts with Kommen/Gehen.
   * Dates in `existingDates` already have entries and are edited from the list instead.
   */
  pickDate?: boolean;
  existingDates?: string[];
}

export default function TimeClockAdminEditSlideOver({
  isOpen,
  onClose,
  entry,
  employeeId,
  employeeName,
  allowsHomeOffice = false,
  pickDate = false,
  existingDates = [],
}: Props) {
  const { t } = useTranslation('timeclock');
  const { correctDay, historyLoading: loading } = useTimeclockAdminStore();
  const isConfirmed = entry?.status === TimeClockDayStatusEnum.NUMBER_2;

  const initialEvents = useMemo<EditableEvent[]>(() => {
    if (pickDate) {
      // A new day starts with the frame every day needs.
      return [TimeClockEventTypeEnum.NUMBER_1, TimeClockEventTypeEnum.NUMBER_2].map((eventType) => ({
        id: crypto.randomUUID(),
        eventType,
        timeValue: '',
        isNew: true,
        workLocation: null,
        vehicleId: null,
        driverRole: null,
      }));
    }
    if (!entry?.events) return [];
    return [...entry.events]
      .sort((a, b) => {
        const at = parseUtcTimestamp(a.timestamp)?.getTime() ?? 0;
        const bt = parseUtcTimestamp(b.timestamp)?.getTime() ?? 0;
        return at - bt;
      })
      .map((evt) => ({
        id: evt.timeClockEventId ?? crypto.randomUUID(),
        eventType: evt.eventType ?? TimeClockEventTypeEnum.NUMBER_1,
        timeValue: utcToLocalTimeInputValue(evt.timestamp),
        isNew: false,
        workLocation: evt.workLocation ?? null,
        vehicleId: evt.vehicleId ?? null,
        driverRole: evt.driverRole ?? null,
      }));
  }, [entry?.events, pickDate]);

  const [events, setEvents] = useState<EditableEvent[]>(initialEvents);
  const [date, setDate] = useState(entry?.date ?? '');
  const dateTaken = pickDate && existingDates.includes(date);
  const [correctionNote, setCorrectionNote] = useState('');
  const [saveError, setSaveError] = useState<string | null>(null);

  useEffect(() => {
    setEvents(initialEvents);
    setCorrectionNote('');
    setSaveError(null);
    setDate(entry?.date ?? '');
    // why: the add-day dialog stays mounted — reopening it must start from a blank day.
  }, [initialEvents, entry?.date, isOpen]);

  const hasChanges = useMemo(() => {
    if (events.length !== initialEvents.length) return true;
    return events.some((evt, i) => {
      const orig = initialEvents[i];
      if (!orig) return true;
      return (
        evt.eventType !== orig.eventType ||
        evt.timeValue !== orig.timeValue ||
        evt.workLocation !== orig.workLocation
      );
    });
  }, [events, initialEvents]);

  const handleClose = () => {
    setEvents(initialEvents);
    setCorrectionNote('');
    setSaveError(null);
    onClose();
  };

  const handleAddEvent = () => {
    setEvents((prev) => [
      ...prev,
      {
        id: crypto.randomUUID(),
        eventType: TimeClockEventTypeEnum.NUMBER_1,
        timeValue: '',
        isNew: true,
        workLocation: null,
        vehicleId: null,
        driverRole: null,
      },
    ]);
  };

  const stretchLocations = useMemo(() => stretchLocationsByTime(events), [events]);

  // Old stamps still show the vehicle chosen back then; a correction never touches the logbook (ADR-0027).
  const describeVehicle = useDescribeVehicle();

  const handleWorkLocationChange = (id: string, value: WorkLocation | null) => {
    setEvents((prev) => prev.map((e) => (e.id === id ? { ...e, workLocation: value } : e)));
  };

  const handleRemoveEvent = (id: string) => {
    setEvents((prev) => prev.filter((e) => e.id !== id));
  };

  const handleEventTypeChange = (id: string, value: number) => {
    setEvents((prev) =>
      prev.map((e) => (e.id === id ? { ...e, eventType: value } : e)),
    );
  };

  const handleTimeChange = (id: string, value: string) => {
    setEvents((prev) =>
      prev.map((e) => (e.id === id ? { ...e, timeValue: value } : e)),
    );
  };

  const handleSave = async () => {
    if (!date || dateTaken) return;
    setSaveError(null);

    const emptyTimeEvents = events.filter((e) => !e.timeValue);
    if (emptyTimeEvents.length > 0) {
      setSaveError(t('edit.missingTimes'));
      return;
    }

    try {
      const correctedEvents = events
        .filter((e) => e.timeValue)
        .map((e) => ({
          type: e.eventType as typeof TimeClockEventTypeEnum[keyof typeof TimeClockEventTypeEnum],
          timestamp: localTimeToUtcIso(date, e.timeValue),
          // A kept stamp keeps its id — and with it an old stamp's vehicle and role.
          ...(e.isNew ? {} : { timeClockEventId: e.id }),
          // Only clock-ins carry the work location.
          ...(e.eventType === TimeClockEventTypeEnum.NUMBER_1 && e.workLocation
            ? { workLocation: e.workLocation }
            : {}),
        }));

      await correctDay(
        employeeId,
        date,
        correctedEvents,
        correctionNote || t('edit.correctionDefault'),
      );
      handleClose();
    } catch (error) {
      setSaveError(translateApiError(parseAxiosError(error)));
    }
  };

  const dateFormatted = date
    ? (() => {
        const [y, m, d] = date.split('-');
        return `${d}.${m}.${y}`;
      })()
    : '';

  const titleText = pickDate
    ? `${employeeName ?? ''} – ${t('edit.addDayTitle')}`
    : employeeName
      ? `${employeeName} – ${dateFormatted}`
      : `${t('actions.correct')} – ${dateFormatted}`;

  return (
    <SlideOver
      isOpen={isOpen}
      onClose={handleClose}
      title={titleText}
      size="lg"
      hasUnsavedChanges={hasChanges}
      footer={
        <SlideOverFooter
          onCancel={handleClose}
          onConfirm={handleSave}
          confirmText={t('edit.save')}
          cancelText={t('edit.cancel')}
          loading={loading}
          confirmDisabled={(!hasChanges && !events.some((e) => e.isNew)) || !date || dateTaken}
        />
      }
    >
      <div className="space-y-5">
        {saveError && (
          <div className="flex items-center gap-2 rounded-md border border-red-200 bg-red-50 px-4 py-3">
            <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0" strokeWidth={2} />
            <p className="text-[13px] text-red-700">{saveError}</p>
          </div>
        )}

        {pickDate && (
          <FormField
            label={t('edit.dateLabel')}
            type="date"
            value={date}
            max={isoDate(new Date())}
            onChange={(e) => setDate(e.target.value)}
            error={dateTaken ? t('edit.dateTaken') : undefined}
            required
          />
        )}

        {isConfirmed && (
          <div className="flex items-start gap-2 rounded-md border border-sky-200 bg-sky-50 px-4 py-3">
            <ShieldCheck className="w-4 h-4 text-sky-600 flex-shrink-0 mt-0.5" strokeWidth={2} />
            <div>
              <p className="text-[13px] font-medium text-sky-800">
                {t('edit.confirmedTitle')}
              </p>
              <p className="text-[12px] text-sky-700 mt-0.5">
                {t('edit.confirmedAdmin')}
              </p>
            </div>
          </div>
        )}

        <div>
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-[13px] font-semibold text-slate-900">
              {t('edit.events')}
            </h3>
            <Button
              variant="secondary"
              size="sm"
              onClick={handleAddEvent}
              leftIcon={<Plus className="w-3.5 h-3.5" strokeWidth={2} />}
            >
              {t('edit.addEvent')}
            </Button>
          </div>

          {events.length === 0 ? (
            <div className="text-[13px] text-slate-500 py-4 text-center">
              {t('edit.noEvents')}
            </div>
          ) : (
            <div className="space-y-2">
              {events.map((evt) => (
                <div
                  key={evt.id}
                  className="flex flex-wrap items-center gap-2 sm:gap-3 p-2.5 rounded-md border border-slate-200 bg-white"
                >
                  <Clock className="w-4 h-4 text-slate-400 flex-shrink-0" strokeWidth={2} />
                  <select
                    value={evt.eventType}
                    onChange={(e) =>
                      handleEventTypeChange(evt.id, Number(e.target.value))
                    }
                    aria-label={t('edit.eventType')}
                    className="px-2 py-1 rounded-md bg-white border border-slate-200 text-[13px] text-slate-900 focus:outline-none focus:ring-2 focus:ring-[#00A871]/30 focus:border-[#00A871]/50"
                  >
                    {EVENT_TYPE_VALUES.map((value) => (
                      <option key={value} value={value}>
                        {t(EVENT_TYPE_KEYS[value])}
                      </option>
                    ))}
                  </select>
                  <input
                    type="time"
                    value={evt.timeValue}
                    onChange={(e) => handleTimeChange(evt.id, e.target.value)}
                    className="w-28 px-2 py-1 rounded-md bg-white border border-slate-200 text-[13px] text-slate-900 tabular-nums focus:outline-none focus:ring-2 focus:ring-[#00A871]/30 focus:border-[#00A871]/50"
                  />
                  <div className="flex-1" />
                  {evt.isNew && (
                    <Badge variant="info">
                      {t('edit.new')}
                    </Badge>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleRemoveEvent(evt.id)}
                    title={t('edit.remove')}
                    aria-label={t('edit.remove')}
                    className="p-1.5"
                  >
                    <Trash2 className="w-4 h-4 text-red-600" strokeWidth={2} />
                  </Button>
                  {evt.eventType === TimeClockEventTypeEnum.NUMBER_1 && allowsHomeOffice ? (
                    <div className="basis-full pl-6">
                      <WorkLocationSelect
                        value={evt.workLocation}
                        onChange={(value) => handleWorkLocationChange(evt.id, value)}
                      />
                    </div>
                  ) : (
                    // Only home office is called out — office is the normal case.
                    isHomeOffice(stretchLocations.get(evt)) && (
                      <p className="basis-full pl-6 text-[12px] text-slate-500">
                        {t('workLocation.homeOffice')}
                      </p>
                    )
                  )}
                  {!evt.isNew && evt.driverRole && (
                    <p className="basis-full pl-6 text-[12px] text-slate-500">
                      {describeVehicle({ driverRole: evt.driverRole, vehicleId: evt.vehicleId })}
                    </p>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        <FormField
          label={t('edit.correctionNoteLabel')}
          value={correctionNote}
          onChange={(e) => setCorrectionNote(e.target.value)}
          placeholder={t('edit.correctionNotePlaceholder')}
        />

        {entry?.correctionNote && (
          <div className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3">
            <p className="text-[11px] font-medium text-amber-800 mb-1">
              {t('edit.lastCorrection')}
            </p>
            <p className="text-[13px] text-amber-800">
              {entry.correctionNote}
            </p>
          </div>
        )}
      </div>
    </SlideOver>
  );
}
